<template>
  <button
    :class="[
      'copyable align-center flex-row justify-between gap-2',
      border && 'copyable--border',
    ]"
    :shrink="iconOnly"
    @click.stop="onClick"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <OwnType
      v-if="!iconOnly"
      class="text--truncate"
      :text="label ?? value"
      :variant="variant"
      :monospace="monospace"
      :value="label ?? value"
      color="inherit"
    />
    <template v-if="showIcon">
      <slot name="icon">
        <PhClipboardText class="flex-shrink-0" size="16" />
      </slot>
    </template>
  </button>
</template>

<script lang="ts" setup>
import { PhClipboardText } from '@phosphor-icons/vue'
import { useClipboard } from '@vueuse/core'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { type FontType } from '@/ui/Common/fonts'
import { OwnType } from '@/ui/OwnType'
import { useNotify } from '@/utils/useNotify'

const props = withDefaults(
  defineProps<{
    border?: boolean
    iconBehavior?: 'show' | 'hover'
    iconOnly?: boolean
    label?: string
    monospace?: boolean
    successMessage?: string
    variant?: FontType
    value: string
  }>(),
  {
    border: true,
    iconBehavior: 'show',
    iconOnly: false,
    label: undefined,
    monospace: false,
    successMessage: undefined,
    variant: 'paragraph-regular',
  }
)

const isHovering = ref(false)

const notify = useNotify()
const { t } = useI18n()
const { copy } = useClipboard()

const onClick = async () => {
  await copy(props.value)
  notify(props.successMessage ?? t('copy.success'), 'success')
}

const onMouseEnter = () => {
  isHovering.value = true
}
const onMouseLeave = () => {
  isHovering.value = false
}

const showIcon = computed(() => {
  if (props.iconBehavior === 'show') return true

  return isHovering.value
})
</script>

<style lang="scss" scoped>
@use '@/styles/globals';

.copyable {
  padding: 4px;
  color: var(--text-color-secondary);
  transition: globals.transitionOne(border);

  &--border {
    border: 1px dashed var(--divider);
    border-radius: 4px;

    &:hover {
      border: 1px solid var(--divider);
    }
  }

  &:active {
    color: rgba(var(--raw-text-color-secondary), 60%);
  }
}
</style>
